import { motion } from 'framer-motion';
import { useMemo } from 'react';
import { ProGate } from '../../components';
import { useAppData } from '../../data';
import { springGentle } from '../../tokens';
import { CashFlowChart } from './CashFlowChart';
import { CategoryTrends } from './CategoryTrends';
import { ForecastCard } from './ForecastCard';
import { NetWorthCard } from './NetWorthCard';
import { SavingsProgress } from './SavingsProgress';
import { SpendingByCategory } from './SpendingByCategory';
import { SpendingTrend } from './SpendingTrend';
import { WeeklySummary } from './WeeklySummary';

const fadeUp = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0 },
};

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.06 } },
};

type AnalyticsScreenProps = {
  onCategoryTap?: (categoryId: string) => void;
};

/**
 * Insights tab. Net Worth sits at the top, followed by Spending by
 * Category, Weekly Summary, Spending Trend, the 30-day Forecast,
 * 6-month Cash Flow, Category Trends, and Savings Progress (PYF only).
 *
 * Forecast and Net Worth are intentionally NOT Pro-gated yet — that
 * step lands in a later pass. CashFlow and CategoryTrends remain
 * gated as advanced insights.
 */
export function AnalyticsScreen({ onCategoryTap }: AnalyticsScreenProps) {
  const data = useAppData();
  const today = useMemo(() => new Date(), []);
  const isPyf = data.profile.budgetModel === 'pyf';

  return (
    <motion.main
      initial="hidden"
      animate="show"
      variants={stagger}
      className="mx-auto max-w-content px-screen-x pt-section-gap pb-section-gap"
    >
      <motion.header
        variants={fadeUp}
        transition={springGentle}
        className="mb-section-gap"
      >
        <h1 className="text-section">Insights</h1>
        <p className="mt-1 text-[13px] text-text-secondary">
          Where your money's going and how it's trending.
        </p>
      </motion.header>

      <motion.div variants={fadeUp} transition={springGentle} className="mb-card-gap">
        <NetWorthCard />
      </motion.div>

      <motion.div variants={fadeUp} transition={springGentle} className="mb-card-gap">
        <SpendingByCategory
          data={data}
          today={today}
          onCategoryTap={onCategoryTap}
        />
      </motion.div>

      <motion.div variants={fadeUp} transition={springGentle} className="mb-card-gap">
        <WeeklySummary data={data} today={today} />
      </motion.div>

      <motion.div variants={fadeUp} transition={springGentle} className="mb-card-gap">
        <SpendingTrend data={data} today={today} />
      </motion.div>

      <motion.div variants={fadeUp} transition={springGentle} className="mb-card-gap">
        <ForecastCard />
      </motion.div>

      <motion.div variants={fadeUp} transition={springGentle} className="mb-card-gap">
        <ProGate feature="advancedInsights">
          <CashFlowChart data={data} today={today} />
        </ProGate>
      </motion.div>

      <motion.div variants={fadeUp} transition={springGentle} className="mb-card-gap">
        <ProGate feature="advancedInsights">
          <CategoryTrends data={data} today={today} />
        </ProGate>
      </motion.div>

      {isPyf ? (
        <motion.div
          variants={fadeUp}
          transition={springGentle}
          className="mb-card-gap"
        >
          <SavingsProgress data={data} today={today} />
        </motion.div>
      ) : null}
    </motion.main>
  );
}
